import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES } from 'screens/Finder/components/views/GalleryView/constants'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import { Upload } from 'blaze-ui/icons/20/Upload'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { bytesToGb } from 'blaze-components/MediaUploadModal/utils'
import { Button } from 'blaze-ui/Button'
import { ButtonProps } from 'blaze-ui/Button/Types'
import { useState } from 'react'
import { ProcessingToastMessage } from 'components/ImageMenu/ImageMenuModal/ProcessingToastMessage'
import Styles from './UploadButton.module.scss'

export const UploadButton = ({
  variant = 'secondary',
  fullWidth = false,
  label = 'Upload',
  size = 'lg',
}: Pick<ButtonProps, 'variant' | 'fullWidth' | 'size'> & {
  label?: string
}) => {
  const [progress, setProgress] = useState(0)
  const { isUploading, fileInputRef, uploadImagesFromEvent, selectInput } = useImportMediaFiles(
    true,
    () => {
      showToastMessage('All files were uploaded successfully!', 'success')
      setProgress(0)
    },
    setProgress,
    { forceExtractBackgroundJobSync: true },
  )
  const currentWorkspaceId = useCurrentWorkspaceId()
  const uploadsProjectQuery = useUploadsProject(currentWorkspaceId)
  const uploadsProject = uploadsProjectQuery?.data?.project || null

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])

    if (files.some((file) => bytesToGb(file.size) > 1)) {
      showToastMessage('Files need to be under 1GB', 'error')
    } else {
      uploadImagesFromEvent(event, uploadsProject?.id, undefined, false)
    }

    // reset input value afterwards
    event.target.value = ''
  }

  return (
    <>
      <Button
        size={size}
        variant={variant}
        frontIcon={Upload}
        onPress={() => selectInput()}
        isDisabled={isUploading}
        fullWidth={fullWidth}
      >
        {label}
      </Button>
      {isUploading && <ProcessingToastMessage percentage={progress} className={Styles.processingToastMessage} />}
      <VisuallyHidden>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={onChange}
          accept={ACCEPTED_IMAGE_AND_VIDEO_MIME_TYPES}
          data-testid="upload-input"
        />
      </VisuallyHidden>
    </>
  )
}
